import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

interface MarkdownPageProps {
  title?: string;
  subtitle?: string;
}

const MarkdownPage: React.FC<MarkdownPageProps> = ({ title, subtitle }) => {
  const { robotName } = useParams<{ robotName: string }>();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/markdown/${robotName?.toLowerCase()}.md`);
        
        if (!response.ok) {
          throw new Error(`Failed to load markdown file: ${response.statusText}`);
        }
        
        const content = await response.text();
        setMarkdownContent(content);
        setError('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load content');
        setMarkdownContent('# Page Not Found\n\nThe requested page could not be found.');
      } finally {
        setLoading(false);
      }
    };

    if (robotName) {
      loadMarkdown();
    }
  }, [robotName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent p-0 relative bg-gray-900">
        <div className="max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto p-8 md:p-12 lg:p-16 py-16 flex flex-col justify-start relative">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-400 mx-auto mb-4"></div>
            <p className="text-gray-300">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent p-0 relative bg-gray-900">
      <div className="max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto p-8 md:p-12 lg:p-16 py-16 flex flex-col justify-start relative">
        {/* Header Section */}
        <div className="text-center mb-8 animate-fadeInUp">
          <div className="mb-6 flex justify-center">
            <a href="/" className="block transition-all duration-300 hover:scale-110">
              <img 
                src="/l5_simple.svg" 
                alt="Level 5 Robotics Logo"
                className="w-20 h-20 transition-all duration-300 object-contain"
              />
            </a>
          </div>
          
          {/* Back to Robots Link - Moved to top */}
          <div className="mb-4">
            <a href="/rival" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm md:text-base">
              ← Back to Robots
            </a>
          </div>
          
          {title && (
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-lg md:text-xl text-gray-300 mb-6 font-medium">{subtitle}</p>
          )}
        </div>

        {/* Markdown Content */}
        <div className="bg-gray-800/90 border border-red-500/20 rounded-xl p-6 md:p-8 backdrop-blur-sm animate-fadeInUp">
          <div className="prose prose-invert prose-red max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-3xl md:text-4xl font-bold text-red-400/90 mb-4">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl md:text-3xl font-bold text-red-400/90 mb-3 mt-6">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl md:text-2xl font-semibold text-red-400/90 mb-2 mt-4">{children}</h3>
                ),
                p: ({ children }) => (
                  <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-1">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="text-gray-300">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-white">{children}</strong>
                ),
                em: ({ children }) => (
                  <em className="italic text-gray-200">{children}</em>
                ),
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    className="text-red-400 hover:text-red-300 underline transition-colors duration-200"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-red-500/50 pl-4 italic text-gray-300 mb-4">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="bg-gray-700/50 text-red-300 px-2 py-1 rounded text-sm font-mono">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-gray-700/50 p-4 rounded-lg overflow-x-auto mb-4">
                    {children}
                  </pre>
                ),
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-600/30">
          <a href="/" className="text-gray-400 hover:text-white transition-colors duration-300">← Back to Home</a>
        </div>
      </div>
    </div>
  );
};

export default MarkdownPage;